import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Spinner from "../components/Spinner";
import AppIcon from "../components/AppIcon";
import LicenseUsageBar from "../components/LicenseUsageBar";
import SearchBar from "../components/SearchBar";
import vppCatalogService from "../services/vppCatalogService";
import deviceGroupService from "../services/deviceGroupService";
import isAdministrator from "../utilities/checkPrivileges";
import {
  APP_BLACKLIST_GRACE_DAYS,
  licenseStatusClass,
  licenseStatusLabel,
} from "../utils/appBlacklist";

const emptyConfigForm = {
  name: "",
  description: "",
  valuesText: "{\n  \n}",
};

function formatBytes(n) {
  const bytes = Number(n);
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE", { dateStyle: "medium" });
}

export default function ApplicationDetail() {
  const { adamId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();

  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [groups, setGroups] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [licenseQuery, setLicenseQuery] = useState("");
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [removeApp, setRemoveApp] = useState(true);
  const [forceRemove, setForceRemove] = useState(false);
  const [disableAfterRemoval, setDisableAfterRemoval] = useState(false);
  const [busy, setBusy] = useState(false);

  const [assignSelection, setAssignSelection] = useState([]);
  const [configForm, setConfigForm] = useState(emptyConfigForm);
  const [editingConfigId, setEditingConfigId] = useState(null);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [assetRes, groupRows, configRows, licenseRes] = await Promise.all([
        vppCatalogService.getAsset(adamId),
        deviceGroupService.getDeviceGroups(),
        vppCatalogService.getAppConfigs(adamId),
        vppCatalogService.getAppLicenses(adamId).catch(() => ({ licenses: [] })),
      ]);
      const nextAsset = assetRes.asset;
      setAsset(nextAsset);
      setGroups(groupRows || []);
      setConfigs(configRows || []);
      setLicenses(licenseRes.licenses || []);
      setAssignSelection(
        (nextAsset?.assignedGroups || []).map((item) => ({
          groupId: item.groupId,
          managedAppConfig: item.managedAppConfig?.id || "",
        }))
      );
      setSelectedSerials([]);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "App konnte nicht geladen werden.");
      setAsset(null);
    } finally {
      setLoading(false);
    }
  }, [adamId]);

  useEffect(() => {
    if (user && adamId) load();
  }, [user, adamId, load]);

  const visibleLicenses = useMemo(() => {
    const q = licenseQuery.trim().toLowerCase();
    if (!q) return licenses;
    return licenses.filter((row) =>
      [row.deviceName, row.serialNumber, row.assetTag, row.clientUserId, row.udid, ...(row.groups || [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [licenses, licenseQuery]);

  const toggleAssignGroup = (groupId) => {
    const id = String(groupId);
    setAssignSelection((prev) => {
      const exists = prev.some((item) => String(item.groupId) === id);
      if (exists) return prev.filter((item) => String(item.groupId) !== id);
      return [...prev, { groupId: id, managedAppConfig: "" }];
    });
  };

  const visibleSerials = visibleLicenses.map((row) => row.serialNumber).filter(Boolean);
  const allVisibleSelected = visibleSerials.length > 0 && visibleSerials.every((serial) => selectedSerials.includes(serial));

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedSerials((prev) => prev.filter((serial) => !visibleSerials.includes(serial)));
      return;
    }
    setSelectedSerials((prev) => [...new Set([...prev, ...visibleSerials])]);
  };

  const saveAssignments = async () => {
    setBusy(true);
    try {
      await vppCatalogService.putAppAssignments(adamId, {
        groups: assignSelection.map((item) => ({
          groupId: item.groupId,
          managedAppConfig: item.managedAppConfig || null,
        })),
        catalogApp: {
          adamId: asset.adamId,
          bundleId: asset.bundleId,
          name: asset.name,
          pricingParam: asset.pricingParam,
          productType: asset.productType,
        },
      });
      toast.success("Gruppenzuweisung gespeichert.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Zuweisung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    let values = {};
    try {
      values = configForm.valuesText.trim() ? JSON.parse(configForm.valuesText) : {};
    } catch {
      toast.error("Values müssen gültiges JSON sein.");
      return;
    }
    if (!configForm.name.trim()) {
      toast.error("Name ist erforderlich.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: configForm.name.trim(),
        adamId,
        bundleId: asset?.bundleId || "",
        appName: asset?.name || "",
        description: configForm.description.trim(),
        values,
      };
      if (editingConfigId) {
        await vppCatalogService.updateAppConfig(editingConfigId, payload);
        toast.success("Config aktualisiert.");
      } else {
        await vppCatalogService.createAppConfig(payload);
        toast.success("Config angelegt.");
      }
      setConfigForm(emptyConfigForm);
      setEditingConfigId(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Config konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const removeConfig = async (id) => {
    if (!window.confirm("Diese Managed App Config löschen?")) return;
    try {
      await vppCatalogService.deleteAppConfig(id);
      toast.success("Config gelöscht.");
      if (editingConfigId === id) {
        setEditingConfigId(null);
        setConfigForm(emptyConfigForm);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Löschen fehlgeschlagen.");
    }
  };

  const toggleSerial = (serial) => {
    setSelectedSerials((prev) =>
      prev.includes(serial) ? prev.filter((item) => item !== serial) : [...prev, serial]
    );
  };

  const revokeLicenses = async (serialNumbers, { force = forceRemove, disable = disableAfterRemoval } = {}) => {
    const serials = serialNumbers.filter(Boolean);
    if (!serials.length) {
      toast.error("Bitte mindestens eine Lizenz auswählen.");
      return;
    }
    let extra = "";
    if (removeApp) {
      extra = force
        ? " Die App wird sofort vom Gerät entfernt."
        : ` Die App wird auf die Blacklist gesetzt (${APP_BLACKLIST_GRACE_DAYS} Tage Karenz, danach Deinstallation).`;
      if (disable) extra += " Automatische Installation bleibt danach gesperrt.";
    }
    if (!window.confirm(`${serials.length} Lizenz(en) einziehen?${extra}`)) return;
    setBusy(true);
    try {
      await vppCatalogService.disassociateLicenses(adamId, {
        serialNumbers: serials,
        pricingParam: asset?.pricingParam,
        bundleId: asset?.bundleId,
        removeApp,
        forceRemove: force,
        disableAfterRemoval: disable,
      });
      toast.success("Lizenzen eingezogen.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Einzug fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const deviceAppAction = async (udid, action, { disable = false } = {}) => {
    if (!udid) return;
    setBusy(true);
    try {
      if (action === "activate") {
        await vppCatalogService.activateDeviceApp(udid, adamId);
        toast.success("App wieder aktiviert.");
      } else if (action === "blacklist") {
        await vppCatalogService.removeDeviceApp(udid, adamId, {
          disable,
          bundleId: asset?.bundleId,
        });
        toast.success(`Blacklist gesetzt (${APP_BLACKLIST_GRACE_DAYS} Tage Karenz).`);
      } else if (action === "force") {
        await vppCatalogService.removeDeviceApp(udid, adamId, {
          force: true,
          disable,
          bundleId: asset?.bundleId,
        });
        toast.success("Sofortige Deinstallation angestoßen.");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  if (loading) return <Spinner />;
  if (!asset) {
    return (
      <div>
        <Link to="/applications">← Applications</Link>
        <p className="mt-3">App nicht gefunden.</p>
      </div>
    );
  }

  const sizeLabel = formatBytes(asset.fileSizeBytes);
  const rating = asset.averageUserRating != null
    ? `${asset.averageUserRating.toFixed(1)}${asset.userRatingCount ? ` (${asset.userRatingCount})` : ""}`
    : null;

  return (
    <div>
      <p className="mb-3">
        <Link to="/applications">← Applications</Link>
      </p>

      <div className="d-flex gap-3 align-items-start flex-wrap mb-4">
        <AppIcon src={asset.artworkUrl || asset.artworkUrlSmall} name={asset.name} size={112} />
        <div className="flex-grow-1">
          <h2 className="mb-1">
            {asset.name || asset.adamId}
            {asset.isB2BCustomApp ? (
              <span className="badge text-bg-info ms-2 align-middle">Custom B2B</span>
            ) : null}
          </h2>
          <p className="text-muted mb-2">
            {asset.artistName || asset.sellerName || "Unbekannter Hersteller"}
            {asset.sellerName && asset.artistName && asset.sellerName !== asset.artistName
              ? ` · ${asset.sellerName}`
              : ""}
          </p>
          <div className="d-flex flex-wrap gap-3 small">
            {asset.genre && <span><strong>Kategorie:</strong> {asset.genre}</span>}
            {asset.version && <span><strong>Version:</strong> {asset.version}</span>}
            {sizeLabel && <span><strong>Größe:</strong> {sizeLabel}</span>}
            {asset.minimumOsVersion && <span><strong>min. OS:</strong> {asset.minimumOsVersion}</span>}
            {asset.contentRating && <span><strong>Alter:</strong> {asset.contentRating}</span>}
            {rating && <span><strong>Bewertung:</strong> {rating}</span>}
            {asset.price && <span><strong>Preis:</strong> {asset.price}</span>}
            {asset.languages?.length > 0 && (
              <span><strong>Sprachen:</strong> {asset.languages.join(", ")}</span>
            )}
          </div>
          <div className="d-flex flex-wrap gap-3 small mt-2">
            <span>
              <strong>App Identifier:</strong>{" "}
              {asset.bundleId ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  title="Für Blacklist oder Single App Mode kopieren"
                  onClick={() => {
                    navigator.clipboard.writeText(asset.bundleId).then(
                      () => toast.success("App Identifier kopiert."),
                      () => toast.error("Kopieren nicht möglich.")
                    );
                  }}
                >
                  <code>{asset.bundleId}</code>
                </button>
              ) : (
                <span className="text-muted">nicht ermittelt</span>
              )}
            </span>
            <span><strong>Adam-ID:</strong> <code>{asset.adamId}</code></span>
            {asset.supportedPlatforms?.length > 0 && (
              <span><strong>Plattformen:</strong> {asset.supportedPlatforms.join(", ")}</span>
            )}
            {formatDate(asset.currentVersionReleaseDate || asset.releaseDate) && (
              <span><strong>Release:</strong> {formatDate(asset.currentVersionReleaseDate || asset.releaseDate)}</span>
            )}
            {asset.deviceAssignable && <span>Gerätezuweisbar</span>}
            {asset.revocable && <span>Widerrufbar</span>}
          </div>
          {asset.isB2BCustomApp && (
            <p className="small text-muted mt-2 mb-0">
              Nur für diese Organisation zugewiesen, nicht im öffentlichen App Store.
            </p>
          )}
          {asset.storeUrl && !asset.isB2BCustomApp && (
            <a className="btn btn-sm btn-outline-primary mt-3" href={asset.storeUrl} target="_blank" rel="noreferrer">
              Im App Store öffnen
            </a>
          )}
        </div>
      </div>

      {asset.description && (
        <div className="card p-3 mb-4">
          <h3 className="h5 mb-2">Beschreibung</h3>
          <div className="small" style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflow: "auto" }}>
            {asset.description}
          </div>
        </div>
      )}

      {asset.screenshotUrls?.length > 0 && (
        <div className="mb-4 d-flex gap-2 overflow-auto pb-2">
          {asset.screenshotUrls.map((url) => (
            <img key={url} src={url} alt="" style={{ height: 180, borderRadius: 8 }} />
          ))}
        </div>
      )}

      <div className="card p-3 mb-4">
        <LicenseUsageBar
          assigned={asset.assignedCount}
          available={asset.availableCount}
          retired={asset.retiredCount}
          total={asset.totalCount}
        />
      </div>

      <div className="card p-3 mb-4">
        <h3 className="h5 mb-2">Gruppenzuweisung</h3>
        {admin ? (
          <>
            <p className="small text-muted">
              Geräte in den gewählten Gruppen erhalten die App automatisch. Optional eine benannte Config je Gruppe.
            </p>
            {groups.length === 0 ? (
              <p className="text-muted mb-0">
                Noch keine Gruppen. Bitte unter <Link to="/device-groups">Geräte-Gruppen</Link> anlegen.
              </p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {groups.map((grp) => {
                  const selected = assignSelection.find((item) => String(item.groupId) === String(grp._id));
                  return (
                    <div key={grp._id} className="d-flex flex-wrap gap-2 align-items-center">
                      <label className="form-check mb-0" style={{ minWidth: 220 }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Boolean(selected)}
                          onChange={() => toggleAssignGroup(grp._id)}
                        />
                        <span className="form-check-label ms-1">{grp.name}</span>
                      </label>
                      {selected && (
                        <select
                          className="form-select form-select-sm"
                          style={{ maxWidth: 280 }}
                          value={selected.managedAppConfig || ""}
                          onChange={(e) =>
                            setAssignSelection((prev) =>
                              prev.map((item) =>
                                String(item.groupId) === String(grp._id)
                                  ? { ...item, managedAppConfig: e.target.value }
                                  : item
                              )
                            )
                          }
                        >
                          <option value="">Keine Config</option>
                          {configs.map((cfg) => (
                            <option key={cfg._id} value={cfg._id}>{cfg.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <button type="button" className="btn btn-primary mt-3" disabled={busy} onClick={saveAssignments}>
              Zuweisung speichern
            </button>
          </>
        ) : (asset.assignedGroups || []).length ? (
          <ul className="mb-0">
            {asset.assignedGroups.map((item) => (
              <li key={item.groupId}>
                {item.groupName}
                {item.managedAppConfig?.name ? ` · ${item.managedAppConfig.name}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted mb-0">Dieser App ist keine Gruppe zugewiesen.</p>
        )}
      </div>

      <div className="card p-3 mb-4">
        <h3 className="h5 mb-2">Managed App Configs</h3>
        {admin && (
          <>
            <p className="small text-muted">
              Benannte Vorlagen für diese App. Platzhalter:{" "}
              <code>{"{{FULLNAME}}"}</code>, <code>{"{{SERIALNUMBER}}"}</code>, <code>{"{{DEVICEUUID}}"}</code>,{" "}
              <code>{"{{ASSETTAG}}"}</code>, <code>{"{{LOCATION-ID}}"}</code>, <code>{"{{LOCATION-NAME}}"}</code>,{" "}
              <code>{"{{NOTES}}"}</code>.
            </p>
            <form onSubmit={saveConfig} className="mb-3">
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label">Name *</label>
                  <input
                    className="form-control"
                    value={configForm.name}
                    onChange={(e) => setConfigForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Admin-Konfiguration"
                    required
                  />
                </div>
                <div className="col-md-8">
                  <label className="form-label">Beschreibung</label>
                  <input
                    className="form-control"
                    value={configForm.description}
                    onChange={(e) => setConfigForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Values (JSON / UserDefaults)</label>
                  <textarea
                    className="form-control font-monospace"
                    rows={5}
                    value={configForm.valuesText}
                    onChange={(e) => setConfigForm((prev) => ({ ...prev, valuesText: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-2 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {editingConfigId ? "Config aktualisieren" : "Config anlegen"}
                </button>
                {editingConfigId && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setEditingConfigId(null);
                      setConfigForm(emptyConfigForm);
                    }}
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </>
        )}
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Beschreibung</th>
              {admin && <th></th>}
            </tr>
          </thead>
          <tbody>
            {configs.length === 0 ? (
              <tr><td colSpan={admin ? 3 : 2} className="text-muted">Noch keine Configs für diese App.</td></tr>
            ) : (
              configs.map((cfg) => (
                <tr key={cfg._id}>
                  <td>{cfg.name}</td>
                  <td>{cfg.description || "—"}</td>
                  {admin && (
                    <td className="text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => {
                          setEditingConfigId(cfg._id);
                          setConfigForm({
                            name: cfg.name || "",
                            description: cfg.description || "",
                            valuesText: JSON.stringify(cfg.values || {}, null, 2),
                          });
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeConfig(cfg._id)}>
                        Löschen
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="card p-3 mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <h3 className="h5 mb-1">Lizenzen</h3>
            <p className="small text-muted mb-0">
              Aktuelle VPP-Zuweisungen dieser App. Einzug gibt die Lizenz in den Pool zurück.
            </p>
          </div>
          {admin && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={busy || selectedSerials.length === 0}
              onClick={() => revokeLicenses(selectedSerials)}
            >
              Ausgewählte einziehen ({selectedSerials.length})
            </button>
          )}
        </div>
        {admin && (
          <div className="mb-2">
            <div className="form-check">
              <input
                id="removeAppOnRevoke"
                type="checkbox"
                className="form-check-input"
                checked={removeApp}
                onChange={(e) => setRemoveApp(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="removeAppOnRevoke">
                App beim Einzug vom Gerät entfernen (Blacklist mit {APP_BLACKLIST_GRACE_DAYS}-Tage-Karenz)
              </label>
            </div>
            {removeApp ? (
              <>
                <div className="form-check">
                  <input
                    id="forceRemoveOnRevoke"
                    type="checkbox"
                    className="form-check-input"
                    checked={forceRemove}
                    onChange={(e) => setForceRemove(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="forceRemoveOnRevoke">
                    Sofort deinstallieren (ohne Karenzzeit)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    id="disableAfterRemoval"
                    type="checkbox"
                    className="form-check-input"
                    checked={disableAfterRemoval}
                    onChange={(e) => setDisableAfterRemoval(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="disableAfterRemoval">
                    Automatische Installation nach Deinstallation sperren
                  </label>
                </div>
              </>
            ) : null}
          </div>
        )}
        <div className="mb-2" style={{ maxWidth: 320 }}>
          <SearchBar searchHandler={setLicenseQuery} value={licenseQuery} placeholder="Gerät oder Seriennummer" />
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                {admin && (
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allVisibleSelected}
                      disabled={visibleSerials.length === 0}
                      onChange={toggleAllVisible}
                      aria-label="Alle sichtbaren Lizenzen auswählen"
                    />
                  </th>
                )}
                <th>Gerät</th>
                <th>Seriennummer</th>
                <th>Standort</th>
                <th>Gruppen</th>
                <th>App-Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleLicenses.length === 0 ? (
                <tr><td colSpan={admin ? 7 : 6} className="text-muted">Keine Lizenzen zugewiesen.</td></tr>
              ) : (
                visibleLicenses.map((row) => (
                  <tr key={`${row.serialNumber || row.clientUserId}-${row.pricingParam}`}>
                    {admin && (
                      <td>
                        {row.serialNumber ? (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedSerials.includes(row.serialNumber)}
                            onChange={() => toggleSerial(row.serialNumber)}
                          />
                        ) : null}
                      </td>
                    )}
                    <td>
                      {row.path ? (
                        <Link to={row.path}>{row.deviceName || row.serialNumber}</Link>
                      ) : (
                        row.deviceName || row.clientUserId || "—"
                      )}
                      {row.assetTag && <div className="small text-muted">{row.assetTag}</div>}
                    </td>
                    <td><code>{row.serialNumber || "—"}</code></td>
                    <td>
                      {row.location
                        ? `${row.location.schoolNumber || ""} ${row.location.name || ""}`.trim()
                        : "—"}
                    </td>
                    <td>{row.groups?.length ? row.groups.join(", ") : "—"}</td>
                    <td>
                      <span className={`badge ${licenseStatusClass(row)}`}>
                        {licenseStatusLabel(row)}
                      </span>
                    </td>
                    <td className="text-nowrap">
                      {admin && row.serialNumber ? (
                        <div className="d-flex flex-wrap gap-1">
                          {row.blacklisted ? (
                            <>
                              {row.udid ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  disabled={busy}
                                  onClick={() => deviceAppAction(row.udid, "activate")}
                                >
                                  Aktivieren
                                </button>
                              ) : null}
                              {row.udid ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={busy}
                                  onClick={() => {
                                    if (!window.confirm("App sofort deinstallieren?")) return;
                                    deviceAppAction(row.udid, "force", { disable: row.disabled });
                                  }}
                                >
                                  Sofort deinstallieren
                                </button>
                              ) : null}
                            </>
                          ) : row.udid ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-warning"
                              disabled={busy}
                              onClick={() => {
                                if (!window.confirm(`App auf Blacklist setzen (${APP_BLACKLIST_GRACE_DAYS} Tage Karenz)?`)) return;
                                deviceAppAction(row.udid, "blacklist");
                              }}
                            >
                              Blacklist
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={busy}
                            onClick={() => revokeLicenses([row.serialNumber])}
                          >
                            Einziehen
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
